'use client'
import * as React from 'react'
import {
  CardStackMinusIcon,
  CommitIcon,
  Cross1Icon,
  Cross2Icon,
  GearIcon,
  InfoCircledIcon,
  Link2Icon,
  Pencil1Icon,
  PlusIcon,
} from '@radix-ui/react-icons'
import { clsx } from 'clsx'
import _omit from 'lodash.omit'
import {
  SimpleTooltip,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '~/sotano-kit/primitives/tooltip'
import { CSS } from '@dnd-kit/utilities'
import { match } from 'ts-pattern'

import { useDashboardSlugs } from '~/hooks/use-dashboard-slugs'
import {
  ParsedTab,
  Tab,
  parseTab,
  parseTabUrl,
  getTabsScrollTop,
  useTabsStore,
  useTabsStoreRoot,
  checkTabExists,
} from './store'
import { useWorkingTreeStore } from '~/hooks-data/blocks-tree/provider'
import { isHotkey } from 'is-hotkey'
import { useRepoStore } from '../repo-store'
import { BlockIcon } from '~/components/block-icon'
import { Button } from '~/sotano-kit/primitives/button'
import { useRepoReplicache } from '~/hooks-data/use-repo-replicache'
import { useUserPreferences } from '~/hooks-data/use-user-preferences'
import { IconButton } from '~/sotano-kit/primitives/icon-button'

import { useWorkingDiff } from '~/lib/diff/use-working-diff'
import { ContextMenuAction } from '~/sotano-kit/primitives/tree'
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuTrigger,
} from '~/sotano-kit/primitives/context-menu'
import { TabContextMenuActions } from './tab-actions'
import {
  SortableContext,
  horizontalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable'
import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  DragOverlay,
  DragStartEvent,
} from '@dnd-kit/core'
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers'
import { toast } from 'sonner'
import { getBlockDisplayData } from '@basehub/data/replicache/mutators/repo-space/block/helpers/component-helpers'
import { useUserStore } from '~/app/_store/user-store'
import { SidebarClosedIcon } from '~/components/icons/sidebar-closed'
import { useHasRendered } from '~/hooks/use-has-rendered'
import { useIsPropertiesPanelCollapsed__PUBLIC } from '../properties-panel/pp-layout'

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '~/sotano-kit/primitives/dropdown-menu'
import { getId } from '@basehub/utils/id/index'
import {
  useIsExternalVisitor,
  useIsGuestInHisBranch,
  useIsMemberOrHigher,
} from '~/hooks-data/use-user-role'
import { idToSourceBlockId } from '@basehub/data/replicache/mutators/repo-space/block/mutators/copy-on-write'
import {
  useBranchOrCommitSegment,
  useSelectedBranch,
} from '~/lib/commits/use-selected-branch-or-commit'
import { BranchIcon } from '~/components/icons/branch-icon'
import { ChangelogIcon } from '~/components/icons/changelog-icon'
import { useQueryClient } from '@tanstack/react-query'
import {
  isComponentBlock,
  isPlayground,
  prettifyBlockType,
} from '@basehub/data/replicache/index'
import { useBranchMergeDiff } from '../../../branch/_hooks/use-merge-diff'
import { DevIcon } from '~/components/icons/dev'
import { TimelineTabIcon } from '../../../branch/_components/timeline/tab-icon'
import { READMEIcon } from '~/components/icons/readme'
import { ConnectIcon } from '~/components/icons/connect'
import { APIExplorerIcon } from '~/components/icons/api-explorer'
import { StatusIcon } from '~/components/icons/status'
import { BlockIconStoreProvider } from '~/components/block-icon/store'
import { useCustomSyncerSub } from '~/replicache/frontend/use-custom-syncer-sub'

export const Tabs = () => {
  const {
    tabs,
    removeTab,
    selectTab,
    activeTab,
    forceActiveTabOntoURL,
    selectPrevTab,
    selectNextTab,
  } = useTabsStore((s) => s)
  const tabsStore = useTabsStoreRoot()
  const { teamSlug, repoSlug, activeTabKey, repoPage } = useDashboardSlugs()
  const { encodedSegment } = useBranchOrCommitSegment()

  const workingTree = useWorkingTreeStore((s) => s.tree)
  const { diff } = useWorkingDiff()
  const blockTreeManagerRef = React.useRef(workingTree)
  // eslint-disable-next-line react-compiler/react-compiler
  blockTreeManagerRef.current = workingTree
  const changesTreeManagerManagerRef = React.useRef(diff?.changesTree)
  // eslint-disable-next-line react-compiler/react-compiler
  changesTreeManagerManagerRef.current = diff?.changesTree
  const [isReadyToDoInitialSync, setIsReadyToDoInitialSync] =
    React.useState(false)
  const parsedTabFromUrl = React.useMemo(
    () => (activeTabKey ? parseTabUrl(activeTabKey) : null),
    [activeTabKey]
  )

  const { data: repoReplicacheData } = useRepoReplicache()

  const loadBlockSyncerEnabled = React.useMemo(() => {
    return (
      workingTree?.root.key &&
      parsedTabFromUrl?.type === 'block' &&
      repoReplicacheData?.replicache
    )
  }, [
    parsedTabFromUrl?.type,
    repoReplicacheData?.replicache,
    workingTree?.root.key,
  ])
  const { data: loadBlocksMeta } = useCustomSyncerSub(
    loadBlockSyncerEnabled ? repoReplicacheData?.replicache : undefined,
    {
      syncer: 'loadBlocks',
      params: {
        rootBlockId: workingTree?.root.key ?? '',
        sourceBlockIds:
          parsedTabFromUrl?.type === 'block' ? [parsedTabFromUrl.blockId] : [],
      },
    }
  )

  React.useEffect(() => {
    let isReady = !!workingTree
    if (parsedTabFromUrl) {
      switch (parsedTabFromUrl.type) {
        case 'block:diff':
          isReady = !!workingTree && !!diff?.changesTree
          break
        default:
          break
      }
    }
    setIsReadyToDoInitialSync(isReady)
  }, [diff?.changesTree, parsedTabFromUrl, workingTree])

  const handleCloseTab = React.useCallback(
    async (key: string) => {
      removeTab([key])
    },
    [removeTab]
  )

  const setFocus = useRepoStore((s) => s.setFocus)

  // 1. initial sync (url changes):
  //   - filter tabs that are not resolved
  //   - init active tab from url
  React.useEffect(() => {
    if (loadBlockSyncerEnabled) {
      if (!loadBlocksMeta?.done) {
        // wait for the syncer to run
        return
      }
    }

    const blockTreeManager = blockTreeManagerRef.current
    const changesTreeManager = changesTreeManagerManagerRef.current
    const searchParams = new URLSearchParams(window.location.search)

    if (!blockTreeManager || !isReadyToDoInitialSync) {
      return
    }

    const filteredTabs: Tab[] = []
    let filteredActiveTab: Tab | null = null

    const { tabs, activeTab } = tabsStore.getState()

    const focusParsedTab = (tab: Tab | null) => {
      const parsedTab = tab ? parseTab(tab) : null
      if (!parsedTab) return
      const hash = window.location.hash.split('?')[0] ?? window.location.hash
      const possibleBlockId =
        hash.replace('#', '') ||
        ('edgeId' in parsedTab ? parsedTab.edgeId : undefined)
      if (possibleBlockId) {
        // focus block from hash
        try {
          setFocus({
            type: 'block:wrapper',
            blockId: idToSourceBlockId(possibleBlockId, blockTreeManager),
          })
        } catch (e) {
          console.error(e)
        }
      }
    }

    // find filteredActiveTab
    if (parsedTabFromUrl) {
      let isValidTab = false

      switch (parsedTabFromUrl.type) {
        case 'block': {
          const foundActiveBlockNode = blockTreeManager.getNode(
            parsedTabFromUrl.blockId,
            { fallbackKey: 'sourceBlockId' }
          )
          const foundActiveBlockPath = blockTreeManager.getPath(
            foundActiveBlockNode?.key,
            { format: 'string' }
          )
          if (!!foundActiveBlockNode && !!foundActiveBlockPath) {
            isValidTab = true
          } else {
            if (!loadBlocksMeta?.done) {
              // wait for the syncer to run before preemptively removing the tab
              return
            }
            isValidTab = false
          }
          break
        }
        case 'block:diff': {
          const foundActiveBlockChangeNode = changesTreeManager?.getNode(
            parsedTabFromUrl.blockId
          )
          isValidTab = !!foundActiveBlockChangeNode
          break
        }
        case 'changelog':
        case 'commit':
        case 'branches':
        case 'merge':
        case 'settings':
        case 'dev':
        case 'agents':
        case 'suggestion':
        case 'timeline': {
          isValidTab = true
          break
        }
        default:
          break
      }

      if (!isValidTab) {
        // this is an invalid state
        // redirect back

        const urlToPush = new URL(
          `/${teamSlug}/${repoSlug}/${repoPage}/${encodedSegment}`,
          window.location.origin
        )

        urlToPush.search = searchParams.toString()
        window.history.pushState({}, '', urlToPush)

        return
      }

      if (activeTab?.key === parsedTabFromUrl.key) {
        // already is active, so lets stop this effect here.
        focusParsedTab(activeTab) // also focus :)
        return
      }

      // always make active
      const tab: Tab = {
        key: parsedTabFromUrl.key,
        partiallyOpen: parsedTabFromUrl.partiallyOpen,
      }

      const existingTab = tabs.find((t) => checkTabExists(t, tab))

      if (!existingTab) filteredTabs.push(tab)
      else tab.scrollTop = existingTab.scrollTop

      filteredActiveTab = existingTab ?? tab
    }

    tabs.forEach((t) => {
      const isActive = t.key === activeTab?.key
      const isAlreadyProcessed = filteredTabs.some((tab) =>
        checkTabExists(tab, t)
      )
      if (isAlreadyProcessed) return
      const parsedTab = parseTab(t)
      switch (parsedTab.type) {
        case 'block': {
          const blockNode = blockTreeManager.getNode(parsedTab.blockId, {
            fallbackKey: 'sourceBlockId',
          })

          if (!blockNode) return
          filteredTabs.push(t)
          if (!filteredActiveTab && isActive) filteredActiveTab = t // fallback
          return
        }
        case 'block:diff': {
          const changeNode = changesTreeManager?.getNode(parsedTab.blockId)

          if (!changeNode) {
            return
          }
          filteredTabs.push(t)
          if (!filteredActiveTab && isActive) filteredActiveTab = t // fallback
          return
        }
        case 'merge':
        case 'commit':
        case 'changelog':
        case 'branches':
        case 'settings':
        case 'dev':
        case 'agents':
        case 'suggestion':
        case 'timeline': {
          filteredTabs.push(t)
          if (!filteredActiveTab && isActive) filteredActiveTab = t // fallback
          return
        }
        default:
          return
      }
    })

    // also manage focus
    if (filteredActiveTab) focusParsedTab(filteredActiveTab)

    tabsStore.setState({
      tabs: getTabsScrollTop(
        filteredTabs,
        activeTab ?? filteredActiveTab // we compute the scrollTop of the current activeTab
      ),
      activeTab: filteredActiveTab,
    })
  }, [
    loadBlocksMeta?.done,
    parsedTabFromUrl,
    encodedSegment,
    repoSlug,
    teamSlug,
    isReadyToDoInitialSync,
    repoPage,
    tabsStore,
    setFocus,
    loadBlockSyncerEnabled,
  ])

  // 2. keep tabs synced with url (app changes)
  React.useEffect(() => {
    if (!forceActiveTabOntoURL) return

    const searchParams = new URLSearchParams(window.location.search)

    const urlToPush = new URL(
      `/${teamSlug}/${repoSlug}/${repoPage}/${encodedSegment}`,
      window.location.origin
    )
    const parsedTab = activeTab ? parseTab(activeTab) : null
    if (parsedTab) {
      switch (parsedTab.type) {
        case 'block':
        case 'block:diff':
          if (parsedTab.key === activeTabKey) return
          urlToPush.pathname += `/${parsedTab.key}`
          urlToPush.search = searchParams.toString()
          urlToPush.hash = parsedTab.edgeId ? parsedTab.edgeId : ''

          break
        case 'merge':
        case 'dev':
        case 'commit':
        case 'timeline':
        case 'settings':
        case 'agents':
        case 'changelog':
        case 'suggestion':
        case 'branches':
          if (parsedTab.key === activeTabKey) return
          urlToPush.pathname += `/${parsedTab.key}`
          urlToPush.search = searchParams.toString()

          break
        default:
          break
      }
      window.history.pushState({}, '', urlToPush)
    } else if (activeTabKey) {
      // we need to clean this up
      // remove block query

      urlToPush.search = searchParams.toString()
      window.history.pushState({}, '', urlToPush)
    }
    // sort of a cleanup
    tabsStore.setState({ forceActiveTabOntoURL: false })
  }, [
    activeTab,
    activeTabKey,
    encodedSegment,
    forceActiveTabOntoURL,
    repoPage,
    repoSlug,
    teamSlug,
    tabsStore,
  ])

  const hasRendered = useHasRendered()

  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (isHotkey('control+option+ArrowLeft', e)) {
        selectPrevTab()
      } else if (isHotkey('control+option+ArrowRight', e)) {
        selectNextTab()
      } else if (isHotkey('control+option+w', e) && activeTab) {
        handleCloseTab(activeTab.key)
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [selectNextTab, selectPrevTab, handleCloseTab, activeTab])

  const handleTabChange = React.useCallback(
    (tabKey: string) => {
      selectTab(tabs.find((t) => t.key === tabKey) ?? null, true)
    },
    [selectTab, tabs]
  )

  const [tabsInUi, setTabsInUi] = React.useState<Tab[]>([])

  React.useLayoutEffect(() => {
    const allTabs = tabs
      // this way, not only we don't show tabs for blocks that don't exist, but also we can calc the gridTemplateColumns correctly
      .filter((t) => {
        const parsedTab = parseTab(t)
        return (
          parsedTab.type !== 'timeline' &&
          match(parsedTab)
            .with({ type: 'block' }, (res) =>
              Boolean(
                workingTree?.getNode(res.blockId, {
                  fallbackKey: 'sourceBlockId',
                })
              )
            )
            .with({ type: 'block:diff' }, (res) => {
              const node = diff?.changesTree.getNode(res.blockId)
              return node
            })
            .with({ type: 'dev' }, (res) => {
              return res.tab === 'api-explorer' || res.tab === 'connect'
            })
            .with({ type: 'settings' }, (res) => {
              return res.tab === 'general' || res.tab === 'public-info'
            })
            .with({ type: 'agents' }, (res) => {
              return res.tab === 'settings'
            })
            .with({ type: 'commit' }, () => true)
            .with({ type: 'merge' }, () => true)
            .with({ type: 'changelog' }, () => true)
            .with({ type: 'branches' }, () => true)
            .with({ type: 'suggestion' }, () => true)
            .otherwise(() => false)
        )
      })

    // if (
    //   activeTab?.key &&
    //   activeTab.key !== 'timeline' &&
    //   allTabs.find((t) => t.key === activeTab.key) === undefined
    // ) {
    //   // clear up active tab
    //   removeTab([activeTab.key])
    // }

    setTabsInUi(allTabs)
  }, [tabs, workingTree, diff?.changesTree, activeTab, removeTab])

  const closeOtherTabs = React.useCallback(
    (key: string) => {
      const otherTabs = tabs.filter((t) => t.key !== key)
      removeTab(otherTabs.map((t) => t.key))
    },
    [removeTab, tabs]
  )

  const closeTabsAtRight = React.useCallback(
    (key: string) => {
      const index = tabs.findIndex((t) => t.key === key)
      const tabsAtRight = tabs.slice(index + 1)
      removeTab(tabsAtRight.map((t) => t.key))
    },
    [removeTab, tabs]
  )

  const copyLinkToTab = React.useCallback(
    (key: string) => {
      const findedTab = tabs.find((t) => t.key === key)
      if (!findedTab) return
      const parsedTab = parseTab(findedTab)
      const url = new URL(
        `/${teamSlug}/${repoSlug}/${repoPage}/${encodedSegment}/${parsedTab.key}`,
        window.location.origin
      )
      navigator.clipboard.writeText(url.toString())
    },
    [tabs, teamSlug, repoSlug, repoPage, encodedSegment]
  )

  // drag and drop
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    })
  )

  const [draggingTabKey, setDraggingTabKey] = React.useState<string | null>(
    null
  )

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event

    if (over && active.id !== over.id) {
      const oldIndex = tabs.findIndex((t) => t.key === active.id)
      const newIndex = tabs.findIndex((t) => t.key === over.id)
      const newTabs = [...tabs]
      const [removed] = newTabs.splice(oldIndex, 1)
      if (!removed) return
      newTabs.splice(newIndex, 0, removed)
      tabsStore.setState({ tabs: newTabs })
    }
    setDraggingTabKey(null)
  }

  function handleDragStart(event: DragStartEvent) {
    const activeTabId = event.active.id.toString()
    const currentTabIndex = tabs.findIndex((t) => t.key === activeTabId)
    const currentTab = tabs[currentTabIndex]
    setDraggingTabKey(activeTabId)
    if (!currentTab) return
    tabsStore.getState().markTabAsPermanent(activeTabId)
    tabsStore.getState().selectTab(currentTab)
  }

  if (!hasRendered) return null
  return (
    <BlockIconStoreProvider
      getChildrenKeys={(id) => {
        try {
          return (
            workingTree?.getChildrenKeys(id) ??
            diff?.changesTree?.getChildrenKeys(id) ??
            []
          )
        } catch {
          return diff?.changesTree?.getChildrenKeys(id) ?? []
        }
      }}
    >
      <div className="flex h-[var(--tabs-height)] overflow-hidden" id="tabs">
        <PrevActions />
        {Boolean(tabsInUi.length) && (
          <div
            className={clsx('shrink-1 h-[var(--tabs-height)] overflow-hidden')}
            style={{
              display: 'grid',
              gridTemplateColumns: `repeat(${tabsInUi.length}, minmax(${
                100 / tabsInUi.length // kind of a `1fr`, but i can't put `1fr` as the first argument of minmax.
              }%, 200px))`,
            }}
          >
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
              onDragStart={handleDragStart}
            >
              <SortableContext
                items={tabsInUi.map((t) => t.key)}
                strategy={horizontalListSortingStrategy}
              >
                {tabsInUi.map((t, i) => {
                  const parsedTab = parseTab(t)

                  switch (parsedTab.type) {
                    case 'block': {
                      const isActive = activeTab?.key === t.key

                      return (
                        <TabBlock
                          key={t.key}
                          blockId={parsedTab.blockId}
                          tabKey={t.key}
                          partiallyOpen={t.partiallyOpen}
                          parsedTab={parsedTab}
                          onCloseTab={handleCloseTab}
                          onTabChange={handleTabChange}
                          isActive={isActive}
                          index={i}
                          closeOtherTabs={closeOtherTabs}
                          closeTabsAtRight={closeTabsAtRight}
                          copyLinkToTab={copyLinkToTab}
                        />
                      )
                    }
                    case 'block:diff': {
                      const changeNode = diff?.changesTree.getNode(
                        parsedTab.blockId
                      )
                      if (!changeNode) return <React.Fragment key={t.key} />
                      return (
                        <TabUI
                          key={t.key}
                          index={i}
                          tabKey={t.key}
                          partiallyOpen={t.partiallyOpen}
                          parsedTab={parsedTab}
                          onCloseTab={handleCloseTab}
                          onTabChange={handleTabChange}
                          isActive={activeTab?.key === t.key}
                          title={changeNode.value.title || 'Untitled'}
                          icon={
                            <StatusIcon
                              status={changeNode.value.status}
                              className="size-[14px]"
                              blameProps={{
                                sourceBlockId: idToSourceBlockId(
                                  changeNode.value
                                ),
                                rootId: workingTree?.root.value.id ?? null,
                              }}
                            />
                          }
                          closeOtherTabs={closeOtherTabs}
                          closeTabsAtRight={closeTabsAtRight}
                          copyLinkToTab={copyLinkToTab}
                          highlight={changeNode.value.type === 'component'}
                        />
                      )
                    }
                    case 'commit': {
                      return (
                        <TabUI
                          key={t.key}
                          index={i}
                          tabKey={t.key}
                          partiallyOpen={t.partiallyOpen}
                          parsedTab={parsedTab}
                          onCloseTab={handleCloseTab}
                          onTabChange={handleTabChange}
                          isActive={activeTab?.key === t.key}
                          title={parsedTab.commitId}
                          icon={<CommitIcon width={14} height={14} />}
                          closeOtherTabs={closeOtherTabs}
                          closeTabsAtRight={closeTabsAtRight}
                          copyLinkToTab={copyLinkToTab}
                        />
                      )
                    }
                    case 'suggestion': {
                      return (
                        <TabUI
                          key={t.key}
                          index={i}
                          tabKey={t.key}
                          partiallyOpen={t.partiallyOpen}
                          parsedTab={parsedTab}
                          onCloseTab={handleCloseTab}
                          onTabChange={handleTabChange}
                          isActive={activeTab?.key === t.key}
                          title={`Suggestion #${parsedTab.branchId}`}
                          icon={<Pencil1Icon width={14} height={14} />}
                          closeOtherTabs={closeOtherTabs}
                          closeTabsAtRight={closeTabsAtRight}
                          copyLinkToTab={copyLinkToTab}
                        />
                      )
                    }
                    case 'branches': {
                      return (
                        <TabUI
                          key={t.key}
                          index={i}
                          tabKey={t.key}
                          partiallyOpen={t.partiallyOpen}
                          parsedTab={parsedTab}
                          onCloseTab={handleCloseTab}
                          onTabChange={handleTabChange}
                          isActive={activeTab?.key === t.key}
                          title="Branches"
                          icon={<BranchIcon width={14} height={14} />}
                          closeOtherTabs={closeOtherTabs}
                          closeTabsAtRight={closeTabsAtRight}
                          copyLinkToTab={copyLinkToTab}
                        />
                      )
                    }
                    case 'dev': {
                      let icon = <DevIcon />
                      if (parsedTab.tab === 'connect') icon = <ConnectIcon />
                      else if (parsedTab.tab === 'api-explorer') {
                        icon = <APIExplorerIcon />
                      }
                      return (
                        <TabUI
                          key={t.key}
                          index={i}
                          tabKey={t.key}
                          partiallyOpen={t.partiallyOpen}
                          parsedTab={parsedTab}
                          onCloseTab={handleCloseTab}
                          onTabChange={handleTabChange}
                          isActive={activeTab?.key === t.key}
                          title={parsedTab.label}
                          icon={icon}
                          closeOtherTabs={closeOtherTabs}
                          closeTabsAtRight={closeTabsAtRight}
                          copyLinkToTab={copyLinkToTab}
                        />
                      )
                    }
                    case 'settings': {
                      let icon = <GearIcon />
                      if (parsedTab.tab === 'general') icon = <GearIcon />
                      else if (parsedTab.tab === 'public-info') {
                        icon = <InfoCircledIcon />
                      }
                      return (
                        <TabUI
                          key={t.key}
                          index={i}
                          tabKey={t.key}
                          partiallyOpen={t.partiallyOpen}
                          parsedTab={parsedTab}
                          onCloseTab={handleCloseTab}
                          onTabChange={handleTabChange}
                          isActive={activeTab?.key === t.key}
                          title={prettifyBlockType(parsedTab.tab ?? '')}
                          icon={icon}
                          closeOtherTabs={closeOtherTabs}
                          closeTabsAtRight={closeTabsAtRight}
                          copyLinkToTab={copyLinkToTab}
                        />
                      )
                    }
                    case 'agents': {
                      let icon = <GearIcon />
                      if (parsedTab.tab === 'settings') icon = <GearIcon />
                      return (
                        <TabUI
                          key={t.key}
                          index={i}
                          tabKey={t.key}
                          partiallyOpen={t.partiallyOpen}
                          parsedTab={parsedTab}
                          onCloseTab={handleCloseTab}
                          onTabChange={handleTabChange}
                          isActive={activeTab?.key === t.key}
                          title={parsedTab.label}
                          icon={icon}
                          closeOtherTabs={closeOtherTabs}
                          closeTabsAtRight={closeTabsAtRight}
                          copyLinkToTab={copyLinkToTab}
                        />
                      )
                    }
                    case 'changelog': {
                      return (
                        <ChangelogTabUI
                          key={t.key}
                          tabKey={t.key}
                          index={i}
                          isActive={activeTab?.key === t.key}
                          partiallyOpen={t.partiallyOpen}
                          parsedTab={parsedTab}
                          onCloseTab={handleCloseTab}
                          onTabChange={handleTabChange}
                          closeOtherTabs={closeOtherTabs}
                          closeTabsAtRight={closeTabsAtRight}
                          copyLinkToTab={copyLinkToTab}
                        />
                      )
                    }
                    case 'merge': {
                      return (
                        <BranchTabs
                          key={t.key}
                          index={i}
                          t={t}
                          parsedTab={parsedTab}
                          activeTab={activeTab}
                          onCloseTab={handleCloseTab}
                          onTabChange={handleTabChange}
                          closeOtherTabs={closeOtherTabs}
                          closeTabsAtRight={closeTabsAtRight}
                          copyLinkToTab={copyLinkToTab}
                        />
                      )
                    }

                    default:
                      return <React.Fragment key={t.key} />
                  }
                })}
              </SortableContext>
              <DragOverlay modifiers={[restrictToHorizontalAxis]}>
                {draggingTabKey &&
                  (() => {
                    const t = tabs.find((t) => t.key === draggingTabKey)
                    if (!t) return <></>
                    const parsedTab = parseTab(t)
                    return match(parsedTab)
                      .with({ type: 'block' }, (result) => {
                        const blockNode = workingTree?.getNode(result.blockId, {
                          fallbackKey: 'sourceBlockId',
                        })

                        const parent = blockNode?.parentKey
                          ? workingTree?.getNode(blockNode.parentKey)
                          : null
                        if (!blockNode || !workingTree) return <></>

                        const { displayTitle } = getBlockDisplayData({
                          block: blockNode.value,
                          parentBlock: parent?.value,
                        })

                        return (
                          <TabComponent
                            title={displayTitle}
                            isActive={activeTab?.key === draggingTabKey}
                            icon={
                              <BlockIcon
                                block={blockNode.value}
                                iconProps={{
                                  width: 14,
                                  height: 14,
                                  className: 'flex-shrink-0 text-dark-muted',
                                }}
                              />
                            }
                          />
                        )
                      })
                      .with({ type: 'block:diff' }, (result) => {
                        const changeNode = diff?.changesTree.getNode(
                          result.blockId
                        )
                        if (!changeNode) return <></>
                        return (
                          <TabComponent
                            key={t.key}
                            isActive={activeTab?.key === draggingTabKey}
                            title={changeNode.value.title}
                            icon={
                              <StatusIcon
                                status={changeNode.value.status}
                                className="size-[14px]"
                                blameProps={{
                                  sourceBlockId: idToSourceBlockId(
                                    changeNode.value
                                  ),
                                  rootId: workingTree?.root.value.id ?? null,
                                }}
                              />
                            }
                          />
                        )
                      })
                      .with({ type: 'commit' }, (result) => {
                        return (
                          <TabComponent
                            key={t.key}
                            isActive={activeTab?.key === draggingTabKey}
                            title={result.commitId}
                            icon={<CommitIcon width={14} height={14} />}
                          />
                        )
                      })
                      .with({ type: 'suggestion' }, (result) => {
                        return (
                          <TabComponent
                            key={t.key}
                            isActive={activeTab?.key === draggingTabKey}
                            title={`Suggestion #${result.branchId}`}
                            icon={<Pencil1Icon width={14} height={14} />}
                          />
                        )
                      })
                      .with({ type: 'changelog' }, (result) => {
                        return (
                          <TabComponent
                            key={t.key}
                            isActive={activeTab?.key === draggingTabKey}
                            title={
                              result.version
                                ? `Release Notes: ${result.version}`
                                : 'Release Notes'
                            }
                            icon={<ChangelogIcon width={14} height={14} />}
                          />
                        )
                      })
                      .with({ type: 'merge' }, (result) => {
                        return (
                          <BranchTabsOverlay
                            isActive={activeTab?.key === draggingTabKey}
                            key={t.key}
                            parsedTab={result}
                          />
                        )
                      })
                      .with({ type: 'branches' }, () => {
                        return (
                          <TabComponent
                            key={t.key}
                            isActive={activeTab?.key === draggingTabKey}
                            title="Branch"
                            icon={<BranchIcon width={14} height={14} />}
                          />
                        )
                      })
                      .with({ type: 'dev' }, (r) => {
                        return (
                          <TabComponent
                            key={t.key}
                            isActive={activeTab?.key === draggingTabKey}
                            title={r.label}
                            icon={
                              r.tab === 'connect' ? (
                                <ConnectIcon />
                              ) : (
                                <APIExplorerIcon />
                              )
                            }
                          />
                        )
                      })
                      .with({ type: 'agents' }, (r) => {
                        return (
                          <TabComponent
                            key={t.key}
                            isActive={activeTab?.key === draggingTabKey}
                            title={r.label}
                            icon={<GearIcon width={14} height={14} />}
                          />
                        )
                      })
                      .otherwise(() => {
                        return (
                          <TabComponent
                            key={t.key}
                            isActive={activeTab?.key === draggingTabKey}
                            title=""
                            icon={<></>}
                          />
                        )
                      })
                  })()}
              </DragOverlay>
            </DndContext>
          </div>
        )}
        <MoreActions hasTabsInUiOpened={Boolean(tabsInUi.length)} />
      </div>
    </BlockIconStoreProvider>
  )
}

const TabSpacer = ({
  children,
  index,
  className,
  ...props
}: {
  children: React.ReactNode
  index: number
} & React.JSX.IntrinsicElements['div']) => {
  return (
    <div
      style={{ maxWidth: '200px' }}
      className={clsx('flex overflow-hidden', className)}
      {...props}
    >
      {index > 0 && <Divider />}
      {children}
    </div>
  )
}

const TabBlock = ({
  blockId,
  isActive,
  ...rest
}: {
  blockId: string
  isActive: boolean
} & Omit<TabUIProps, 'afterTitle' | 'icon' | 'highlight' | 'title'>) => {
  const blockNode = useWorkingTreeStore((s) => s.tree?.getNode(blockId))
  const parent = useWorkingTreeStore((s) => {
    const pkey = s.tree?.getNode(blockId)?.parentKey
    if (!pkey) return null
    return s.tree?.getNode(pkey)
  })

  if (!blockNode) {
    return <React.Fragment />
  }

  const { displayTitle: title } = getBlockDisplayData({
    block: blockNode.value,
    parentBlock: parent?.value,
    ignoreDisplayTitle: true,
  })
  const { displayTitle: parentTitle } =
    parent?.value && parent?.value.type !== 'root'
      ? getBlockDisplayData({
          block: parent.value,
          parentBlock: null,
          ignoreDisplayTitle: true,
        })
      : { displayTitle: '' }

  return (
    <TabUI
      {...rest}
      isActive={isActive}
      title={title}
      afterTitle={
        parentTitle && (
          <span
            className={clsx(
              'ml-1.5 text-[12px]',
              isActive ? 'text-dark-muted/90' : 'text-dark-muted/50'
            )}
          >
            {parentTitle}
          </span>
        )
      }
      icon={
        <BlockIcon
          block={blockNode.value}
          iconProps={{
            width: 14,
            height: 14,
            className: 'flex-shrink-0',
          }}
        />
      }
      highlight={isComponentBlock(blockNode.value)}
    />
  )
}

type TabUIProps = {
  parsedTab: ParsedTab
  onCloseTab: (key: string) => void
  onTabChange: (key: string) => void
  closeOtherTabs: (key: string) => void
  closeTabsAtRight: (key: string) => void
  copyLinkToTab: (key: string) => void
  partiallyOpen: boolean
  isActive: boolean
  title: string
  afterTitle?: React.ReactNode
  icon?: React.ReactNode
  tabKey: string
  index: number
  highlight?: boolean
}

const TabUI = ({
  isActive,
  onCloseTab,
  onTabChange,
  parsedTab,
  partiallyOpen,
  title,
  afterTitle,
  icon,
  closeOtherTabs,
  closeTabsAtRight,
  copyLinkToTab,
  tabKey,
  index,
  highlight,
}: TabUIProps) => {
  const markTabAsPermanent = useTabsStore((s) => s.markTabAsPermanent)

  const actions: ContextMenuAction[] = React.useMemo(() => {
    return [
      {
        type: 'action',
        label: 'Close tab',
        icon: <Cross1Icon />,
        onSelect: () => {
          onCloseTab(parsedTab.key)
        },
      },
      {
        type: 'action',
        label: 'Close other tabs',
        icon: <CardStackMinusIcon width={16} height={16} />,
        onSelect: () => {
          closeOtherTabs(parsedTab.key)
        },
      },
      {
        type: 'action',
        label: 'Close tabs to the right',
        icon: <CardStackMinusIcon width={16} height={16} />,
        onSelect: () => {
          closeTabsAtRight(parsedTab.key)
        },
      },
      { type: 'separator' },
      {
        type: 'action',
        label: 'Copy link',
        icon: <Link2Icon width={16} height={16} />,
        onSelect: () => {
          copyLinkToTab(parsedTab.key)
          toast.success('Link copied to clipboard')
        },
      },
    ]
  }, [
    closeOtherTabs,
    closeTabsAtRight,
    onCloseTab,
    parsedTab.key,
    copyLinkToTab,
  ])

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: tabKey, strategy: horizontalListSortingStrategy })

  const style = {
    transform: CSS.Transform.toString(
      transform ? { ...transform, y: 0 } : transform
    ),
    transition,
  }

  return (
    <TabContextMenu actions={actions}>
      <TabSpacer
        index={index}
        style={style}
        ref={setNodeRef}
        {...attributes}
        {...listeners}
        className={clsx(
          'relative group/spacer outline-none',
          isDragging && 'invisible'
        )}
      >
        <button
          tabIndex={-1}
          className={clsx(
            'group relative flex w-full cursor-default select-none items-center border-b border-dark-border-solid px-4 pr-2 text-xs',
            isActive && '!border-b-transparent bg-dark-shade',
            isActive && !highlight ? 'text-dark-bright' : 'text-dark-muted',
            'focus:outline-none focus-visible:outline outline-none group-focus-visible/spacer:outline-1 -outline-offset-1 group-focus-visible/spacer:outline-dark-control-base '
          )}
          onFocus={(e) => e.currentTarget.blur()}
          title={title}
          onMouseDown={() => {
            onTabChange(parsedTab.key)
          }}
          onAuxClick={() => {
            onCloseTab(parsedTab.key)
          }}
          onDoubleClick={() => {
            markTabAsPermanent(parsedTab.key)
          }}
        >
          <span className="flex items-center flex-shrink w-full space-x-2 overflow-hidden">
            {icon && (
              <span
                className={clsx(highlight && '!text-dark-control-highlight')}
              >
                {icon}
              </span>
            )}
            <span
              className={clsx(
                'overflow-hidden text-ellipsis whitespace-nowrap',
                partiallyOpen && 'italic',
                'pr-2' // apply padding here because italic may cause cropping.
              )}
            >
              <span
                className={clsx(highlight && '!text-dark-control-highlight')}
              >
                {title}
              </span>
              {afterTitle}
            </span>
          </span>

          <span
            className={clsx(
              'hover:shadow cursor-default rounded p-[3px] text-dark-muted hover:bg-dark-elevated hover:text-dark-dim group-hover:block',
              isActive ? 'block' : 'hidden'
            )}
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              onCloseTab(parsedTab.key)
            }}
            onPointerDown={(e) => {
              e.preventDefault()
              e.stopPropagation()
            }}
            tabIndex={-1}
            role="button"
            title="Close tab"
          >
            <Cross2Icon className="w-3 h-3" />
          </span>

          {/* <span className="invisible absolute -bottom-px left-[-12px] h-px w-full bg-orange8 group-radix-state-active:visible" /> */}
        </button>
      </TabSpacer>
    </TabContextMenu>
  )
}

interface TabContextMenuProps {
  actions: ContextMenuAction[]
  children: React.ReactNode
}

const TabContextMenu = ({ children, actions }: TabContextMenuProps) => (
  <ContextMenu>
    <ContextMenuTrigger asChild>{children}</ContextMenuTrigger>
    <ContextMenuContent>
      <TabContextMenuActions actions={actions} />
    </ContextMenuContent>
  </ContextMenu>
)

const TabComponent = ({
  title,
  icon,
  isActive,
}: {
  title: string
  icon?: React.ReactNode
  isActive: boolean
}) => {
  return (
    <TabSpacer
      index={0}
      className={clsx(
        'relative h-10',
        isActive ? 'bg-dark-shade' : 'bg-dark-bg'
      )}
    >
      <button className="group relative flex w-full select-none items-center h-10 px-4 pr-2 text-xs text-dark-bright cursor-grabbing">
        <div className="flex items-center flex-shrink w-full space-x-2 overflow-hidden">
          {icon && <span>{icon}</span>}
          <span
            className={clsx(
              'overflow-hidden text-ellipsis whitespace-nowrap',
              'pr-2' // apply padding here because italic may cause cropping.
            )}
          >
            {title}
          </span>
        </div>
      </button>
    </TabSpacer>
  )
}

const Divider = ({ style }: React.JSX.IntrinsicElements['div']) => (
  <div style={style} className="w-px h-full shrink-0 bg-dark-border-solid" />
)

const QuickCreateBlockMenu = ({ children }: { children: React.ReactNode }) => {
  const replicache = useRepoReplicache().data?.replicache
  const addTab = useTabsStore((s) => s.addTab)
  const rootBlockId = useWorkingTreeStore((s) => s.tree?.root.value.id)
  const setFocus = useRepoStore((s) => s.setFocus)

  const createAndOpenBlock = React.useCallback(
    async (type: 'list' | 'document' | 'component') => {
      if (!rootBlockId || !replicache) return null

      const { newBlock } = await replicache.mutate.createBlock({
        data: {
          id: getId(),
          title: '',
          type,
        },
        parentInstanceChildTarget: null,
        isoNow: new Date().toISOString(),
        position: { type: 'first' },
        path: rootBlockId,
        idSeed: getId(),
      })

      if (!newBlock) {
        toast.error('Failed to create block, this is a bug in basehub')
      }

      addTab({
        key: newBlock.id,
        partiallyOpen: false,
      })
      setFocus({
        type: 'block:document-title',
        blockId: newBlock.id,
      })
    },
    [replicache, rootBlockId, addTab, setFocus]
  )

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{children}</DropdownMenuTrigger>
      <DropdownMenuContent
        sticky="always"
        sideOffset={6}
        align="start"
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        <DropdownMenuGroup>
          <DropdownMenuItem
            onSelect={async () => await createAndOpenBlock('document')}
            className="space-x-2"
          >
            <BlockIcon block={{ type: 'document' }} />
            <span>Add Document</span>
          </DropdownMenuItem>
          <DropdownMenuItem
            onSelect={async () => await createAndOpenBlock('component')}
            className="space-x-2"
          >
            <BlockIcon block={{ type: 'component' }} />
            <span>Add Component</span>
          </DropdownMenuItem>
          <DropdownMenuItem
            onSelect={async () => await createAndOpenBlock('list')}
            className="space-x-2"
          >
            <BlockIcon block={{ type: 'list' }} />
            <span>Add Collection</span>
          </DropdownMenuItem>
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

const MoreActions = ({ hasTabsInUiOpened }: { hasTabsInUiOpened: boolean }) => {
  const { data: repoReplicacheData } = useRepoReplicache()
  const optimisticUser = useUserStore((s) => s.optimisticUser)
  const collapsePropertiesPanel = useUserPreferences(
    (s) => s.collapsePropertiesPanel
  )
  const isLoadingUserPreferences = useUserPreferences((s) => s.isLoading)
  const isEditMode = useUserPreferences((s) => s.editMode)
  const activeTab = useTabsStore((s) => s.activeTab)
  const parsedActiveTab = activeTab ? parseTab(activeTab) : activeTab

  const [isCollapsed__PUBLIC, setIsCollapsed__PUBLIC] =
    useIsPropertiesPanelCollapsed__PUBLIC()

  let showExpandPropertiesPanelButton = true
  if (isLoadingUserPreferences) {
    showExpandPropertiesPanelButton = isCollapsed__PUBLIC
  } else {
    showExpandPropertiesPanelButton = Boolean(
      collapsePropertiesPanel && parsedActiveTab?.type === 'block'
    )
  }
  const canModifyTheSchema = useIsMemberOrHigher() && !isEditMode

  const itemWidth = 43
  let minContainerWidth: number | undefined = undefined
  if (!canModifyTheSchema && !showExpandPropertiesPanelButton) {
    minContainerWidth = 0
  } else {
    minContainerWidth = 0
    if (canModifyTheSchema) {
      minContainerWidth += itemWidth
    }
    if (showExpandPropertiesPanelButton) {
      minContainerWidth += itemWidth
    }
  }

  /*
    Watch the container's width to decide if there's enough space for the divider;
    we do this since there the scenarios were the divider is necessary are too specific.
    e.g: tabs occupy all the width and we don't have edit access (so there's no plus button)
    aaand we have the properties panel open.
  */
  const containerRef = React.useRef<HTMLDivElement>(null)
  const [showFirstDivider, setShowFirstDivider] = React.useState(false)
  React.useEffect(() => {
    const container = containerRef.current
    if (!container) return
    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const elemSize = entry.contentRect.width
        setShowFirstDivider(elemSize >= 1)
      }
    })

    resizeObserver.observe(container)

    return () => {
      resizeObserver.disconnect()
    }
  }, [])

  return (
    <div
      ref={containerRef}
      className={clsx(
        'shrink-1 flex min-w-[100px] flex-grow items-center border-b border-dark-border-solid'
      )}
      style={{ minWidth: minContainerWidth }}
    >
      <Divider
        style={{
          display: hasTabsInUiOpened && showFirstDivider ? 'block' : 'none',
        }}
      />
      {canModifyTheSchema && (
        <QuickCreateBlockMenu>
          <Button
            focusStyled
            className="flex items-center justify-center flex-shrink-0 h-full cursor-default text-dark-muted hover:text-dark-dim !rounded-none !-outline-offset-1"
            style={{ width: itemWidth }}
            unstyled
          >
            <PlusIcon />
          </Button>
        </QuickCreateBlockMenu>
      )}
      <div
        className="flex items-center justify-center flex-shrink-0 h-full ml-auto border-l -translate-x-px border-dark-border-solid"
        style={{
          display: showExpandPropertiesPanelButton ? 'flex' : 'none',
          width: itemWidth,
        }}
      >
        <Tooltip>
          <TooltipTrigger asChild>
            <IconButton
              disabled={!showExpandPropertiesPanelButton}
              className="text-dark-faint enabled:hover:text-dark-dim group translate-x-px translate-y-[0.5px]"
              onClick={async () => {
                if (!repoReplicacheData?.replicache || !optimisticUser) {
                  setIsCollapsed__PUBLIC(false)
                } else {
                  await repoReplicacheData.replicache.mutate.setCollapsedPropertiesPanel(
                    {
                      userId: optimisticUser.id,
                      isCollapsed: false,
                    }
                  )
                }
              }}
            >
              <SidebarClosedIcon className="text-dark-muted group-enabled:group-hover:text-inherit size-4" />
            </IconButton>
          </TooltipTrigger>
          <TooltipContent side="left" sideOffset={8}>
            {showExpandPropertiesPanelButton
              ? 'Open properties panel'
              : 'Choose a block to open properties panel'}
          </TooltipContent>
        </Tooltip>
      </div>
    </div>
  )
}

const PrevActions = () => {
  const { data: repoReplicacheData } = useRepoReplicache()
  const addTab = useTabsStore((s) => s.addTab)
  const activeTab = useTabsStore((s) => s.activeTab)
  const itemWidth = 48
  const optimisticUser = useUserStore((s) => s.optimisticUser)
  const readmeIsActive = activeTab?.key === 'timeline' || !activeTab
  const isVisitor = useIsExternalVisitor()
  const isGuestInHisBranch = useIsGuestInHisBranch()
  const branch = useSelectedBranch().branch
  const isInPlayground = branch ? isPlayground(branch) : false
  const collapseDocumentPanel = useUserPreferences(
    (s) => s.collapseDocumentPanel
  )

  return (
    <div
      className="flex items-center justify-center flex-shrink-0 h-full"
      style={{ width: collapseDocumentPanel ? itemWidth * 2 : itemWidth }}
    >
      <div
        className="border-b border-dark-border-solid h-full justify-center items-center flex-1"
        style={{ display: collapseDocumentPanel ? 'flex' : 'none' }}
      >
        <Tooltip>
          <TooltipTrigger asChild>
            <IconButton
              className="text-dark-faint hover:text-dark-dim group"
              onClick={async () => {
                if (!repoReplicacheData?.replicache || !optimisticUser) return
                await repoReplicacheData.replicache.mutate.setCollapsedDocumentPanel(
                  {
                    userId: optimisticUser.id,
                    isCollapsed: false,
                  }
                )
              }}
            >
              <SidebarClosedIcon className="text-dark-muted group-hover:text-inherit size-4 rotate-180" />
            </IconButton>
          </TooltipTrigger>
          <TooltipContent side="right" sideOffset={8}>
            Open sidebar
          </TooltipContent>
        </Tooltip>
      </div>
      {collapseDocumentPanel && <Divider />}
      <SimpleTooltip
        content={isVisitor ? 'README' : 'Branch timeline'}
        side="right"
        sideOffset={0}
      >
        <Button
          className={clsx(
            'flex items-center justify-center flex-shrink-0 h-full cursor-default border-b border-r border-r-dark-border-solid !rounded-none !-outline-offset-1',
            !collapseDocumentPanel && '!rounded-tl-[11px]',
            readmeIsActive
              ? 'border-b-transparent text-dark-dim bg-dark-shade'
              : 'text-dark-muted border-dark-border-solid'
          )}
          style={{ width: itemWidth }}
          focusStyled
          onClick={() => {
            addTab({
              key: 'timeline',
              partiallyOpen: false,
            })
          }}
          unstyled
        >
          {isVisitor && !isGuestInHisBranch && !isInPlayground ? (
            <READMEIcon className="size-[18px]" />
          ) : (
            <TimelineTabIcon />
          )}
        </Button>
      </SimpleTooltip>
    </div>
  )
}

const BranchTabs = ({
  activeTab,
  t,
  index,
  parsedTab,
  closeOtherTabs,
  closeTabsAtRight,
  copyLinkToTab,
  onCloseTab,
  onTabChange,
}: {
  activeTab: Tab | null
  t: Tab
  index: number
  parsedTab: Extract<ParsedTab, { type: 'merge' }>
  onCloseTab: (key: string) => void
  onTabChange: (key: string) => void
  closeOtherTabs: (key: string) => void
  closeTabsAtRight: (key: string) => void
  copyLinkToTab: (key: string) => void
}) => {
  const branch = useSelectedBranch().branch ?? null
  const { diffResult } = useBranchMergeDiff({ branch })
  const removeTab = useTabsStore((s) => s.removeTab)

  const shouldRemoveTab = React.useMemo(() => {
    if (diffResult.isLoading) return false
    if (!parsedTab.blockId) return true
    const changeNode = diffResult.diff?.changesTree.getNode(parsedTab.blockId)
    if (!changeNode) return true
    return false
  }, [parsedTab, diffResult.diff?.changesTree, diffResult.isLoading])

  React.useEffect(() => {
    if (!shouldRemoveTab) return
    removeTab([t.key])
  }, [shouldRemoveTab, removeTab, t.key])

  if (!parsedTab.blockId) {
    return <React.Fragment key={t.key} />
  }
  const changeNode = diffResult.diff?.changesTree.getNode(parsedTab.blockId)
  if (!changeNode) return <React.Fragment key={t.key} />

  return (
    <TabUI
      key={t.key}
      index={index}
      tabKey={t.key}
      partiallyOpen={t.partiallyOpen}
      parsedTab={parsedTab}
      onCloseTab={onCloseTab}
      onTabChange={onTabChange}
      isActive={activeTab?.key === t.key}
      title={changeNode.value.title || 'Untitled'}
      icon={
        <StatusIcon
          status={changeNode.value.status}
          className="size-[14px]"
          blameProps={{
            sourceBlockId: idToSourceBlockId(changeNode.value),
            rootId: diffResult.treeNew?.root.value.id ?? null,
          }}
        />
      }
      closeOtherTabs={closeOtherTabs}
      closeTabsAtRight={closeTabsAtRight}
      copyLinkToTab={copyLinkToTab}
    />
  )
}

const BranchTabsOverlay = ({
  parsedTab,
  isActive,
}: {
  parsedTab: Extract<ParsedTab, { type: 'merge' }>
  isActive: boolean
}) => {
  const branch = useSelectedBranch().branch ?? null
  const { diffResult } = useBranchMergeDiff({ branch })

  if (!parsedTab.blockId) {
    return <React.Fragment key={parsedTab.key} />
  }
  const changeNode = diffResult.diff?.changesTree.getNode(parsedTab.blockId)
  if (!changeNode) return <React.Fragment key={parsedTab.key} />
  return (
    <TabComponent
      key={parsedTab.key}
      isActive={isActive}
      title={changeNode.value.title}
      icon={
        <StatusIcon
          status={changeNode.value.status}
          className="size-[14px]"
          blameProps={{
            sourceBlockId: idToSourceBlockId(changeNode.value),
            rootId: diffResult.treeNew?.root.value.id ?? null,
          }}
        />
      }
    />
  )
}

const ChangelogTabUI = (props: Omit<TabUIProps, 'title' | 'icon'>) => {
  const [version, setVersion] = React.useState<string | null>(null)
  const queryClient = useQueryClient()
  const lastTabKey = React.useRef<string | null>(null)

  React.useEffect(() => {
    if (version && lastTabKey.current === props.tabKey) return
    function getVersion() {
      const data = queryClient.getQueryData(props.tabKey.split(':')) as
        | { version: string }
        | undefined
        | null

      if (data?.version) {
        setVersion(data.version)
        lastTabKey.current = props.tabKey
      }
    }

    getVersion()
    const interval = setInterval(getVersion, 1000)

    return () => {
      clearInterval(interval)
    }
  }, [props.tabKey, queryClient, version])

  return (
    <TabUI
      {...props}
      title={version ? `Release Notes: ${version}` : 'Release Notes'}
      icon={<ChangelogIcon width={15} height={15} />}
    />
  )
}
